import {
    PlacementProcessDetailDto,
    PlacementProcessDocumentDto,
    PlacementProcessDto,
    PlacementProcessOriginDto,
    PlacementProcessStatusDto
} from "../../api/sls";
import {makeAutoObservable, reaction} from "mobx";
import {SlsFormData} from "../../components/page/sls/SlsForm";
import {api, catchApi} from "../../api";
import {SlsUserItem, slsUserStore} from "./SlsUserStore";
import {SlsCustomerItem, slsCustomerStore} from "./SlsCustomerStore";
import {validationStore} from "../ValidationStore";
import {SlsPrinterItem, slsPrinterStore} from "./SlsPrinterStore";
import {userPrinterPreferenceStore} from "../ums";

class SlsDetailStore {
    get origin(): PlacementProcessOriginDto | undefined {
        return this._origin;
    }

    set origin(value: PlacementProcessOriginDto | undefined) {
        this._origin = value;
    }

    get printer(): SlsPrinterItem | undefined {
        return this._printer;
    }

    set printer(value: SlsPrinterItem | undefined) {
        this._printer = value;
    }

    get cabinet(): string | undefined {
        return this._cabinet;
    }

    set cabinet(value: string | undefined) {
        this._cabinet = value;
    }

    get cabinets(): string[] {
        return this._cabinets;
    }

    private set cabinets(value: string[]) {
        this._cabinets = value;
    }

    get completable(): boolean {
        return this._completable;
    }

    private set completable(value: boolean) {
        this._completable = value;
    }

    get completing(): boolean {
        return this._completing;
    }

    private set completing(value: boolean) {
        this._completing = value;
    }

    get completableChecking(): boolean {
        return this._completableChecking;
    }

    private set completableChecking(value: boolean) {
        this._completableChecking = value;
    }

    get correlationId(): string {
        return this._correlationId;
    }

    set correlationId(value: string) {
        this._correlationId = value;
    }

    get user(): SlsUserItem | undefined {
        return this._user;
    }

    set user(value: SlsUserItem | undefined) {
        this._user = value;
    }

    get customer(): SlsCustomerItem | undefined {
        return this._customer;
    }

    set customer(value: SlsCustomerItem | undefined) {
        this._customer = value;
    }

    get documents(): PlacementProcessDocumentDto[] | undefined {
        return this._documents;
    }

    set documents(value: PlacementProcessDocumentDto[] | undefined) {
        this._documents = value;
    }

    get data(): PlacementProcessDetailDto | undefined {
        return this._data;
    }

    set data(value: PlacementProcessDetailDto | undefined) {
        this._data = value;
    }

    get id(): string | undefined {
        return this._id;
    }

    set id(value: string | undefined) {
        this._id = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }

    get status(): PlacementProcessStatusDto {
        return this._status || PlacementProcessStatusDto.ACTIVE
    }

    set status(value: PlacementProcessStatusDto | undefined) {
        this._status = value;
    }

    get isStorageTransfer(): boolean {
        return this.data?.data.origin === PlacementProcessOriginDto.STS
    }

    private _valid: boolean = false
    private _completableChecking = false
    private _completable = false
    private _completing = false

    private _id: string | undefined
    private _data: PlacementProcessDetailDto | undefined

    private _correlationId: string = ''
    private _user: SlsUserItem | undefined
    private _customer: SlsCustomerItem | undefined
    private _origin: PlacementProcessOriginDto | undefined
    private _documents: PlacementProcessDocumentDto[] | undefined = []
    private _status: PlacementProcessStatusDto | undefined
    private _cabinets: string[] = []
    private _cabinet: string | undefined
    private _printer: SlsPrinterItem | undefined

    constructor() {
        makeAutoObservable(this)
        reaction(() => [
            userPrinterPreferenceStore.zebraPrinterId,
            slsPrinterStore.options
        ], () => this.resetDefaultPrinter())
    }

    resetDefaultPrinter() {
        const printerId = userPrinterPreferenceStore.zebraPrinterId
        const options = slsPrinterStore.options
        this.printer = options.find(p => p.id === printerId) || undefined
    }

    async load(): Promise<PlacementProcessDetailDto | undefined> {
        const id = this.id!!
        const result = await catchApi(async () => await api.sls.api.getPlacementProcess(id))

        this.data = result?.data

        const cabinetsResult = await catchApi(async () => await api.sls.api.getCabinets(id))
        this.cabinets = cabinetsResult?.data || []

        this.reset()
        if (!this.cabinet && this.cabinets.length === 1) {
            this.cabinet = this.cabinets[0]
        }

        return result?.data
    }

    get formData(): SlsFormData {
        const {
            correlationId,
            user,
            customer,
            documents,
            status,
            origin
        } = this
        return {
            correlationId,
            user,
            customer,
            documents,
            status,
            origin
        }
    }

    set formData(data: SlsFormData) {
        const {
            correlationId,
            user,
            customer,
            documents,
            status,
            origin,
        } = data
        this.correlationId = correlationId
        this.user = user
        this.customer = customer
        this.documents = documents
        this.status = status
        this.origin = origin
    }

    public build(): PlacementProcessDto {
        const {
            correlationId,
            user,
            customer,
            status,
            origin,
            documents
        } = this
        return {
            correlationId: correlationId,
            username: user?.username!!,
            customerId: customer?.id!!,
            documents: documents,
            origin: origin || PlacementProcessOriginDto.SLS,
            status: status
        }
    }

    async validate() {
        this.valid = false
        const dto = this.build()
        await validationStore.validate(api.sls.api.validatePlacementProcess, dto)

        this.valid = Boolean(validationStore.valid)
    }

    async complete() {
        this.completing = true
        const {
            id,
            printer,
            completable,
            completableChecking,
            cabinet
        } = this
        if (!id || !completable || completableChecking || !printer) {
            return
        }
        this.completable = false
        const result = await catchApi(async () => await api.sls.api.completePlacementProcess(id, {
            cabinet,
            printerId: printer.id!
        }))
        this.data = result?.data
        await this.reset()
        this.completing = false
    }

    async checkFinishable() {
        const {id} = this
        if (!id) {
            return
        }
        this.completableChecking = true
        this.completable = false
        const result = await catchApi(async () => api.sls.api.isCompletable(id))
        this.completableChecking = false
        this.completable = result?.data.isCompletable || false
    }

    async reset() {
        const {data} = this.data || {}
        if (data) {
            this.customer = slsCustomerStore.getCustomerFromStoreById(data.customerId)
            this.user = await slsUserStore.getUserFromStoreByUserName(data.username)
            this.origin = data.origin
            this.correlationId = data.correlationId
            this.documents = data.documents
            this.status = data.status
            this.valid = true
            this.printer = undefined
            this.checkFinishable().finally()
        }
    }

    async removeDocument(documentId: string) {
        const id = this.id!!
        await catchApi(async () =>
            await api.sls.api.revokeDocument(id, documentId))
    }
}

const slsDetailStore = new SlsDetailStore()

export {
    slsDetailStore
}
